"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomStyleState = void 0;
const ui_1 = require("@jsonjoy.com/ui");
const json_crdt_1 = require("json-joy/lib/json-crdt");
const guard_1 = require("./guard");
const css_1 = require("./css");
/**
 * Manages the document-level `CustomStyle` stored at `/cs` on `MuTxtState.obj`.
 */
class CustomStyleState {
    constructor(mutxt) {
        this.mutxt = mutxt;
        this.cs = ui_1.rsync.val({});
        this.appliedEditableProps = [];
        this.appliedBodyProps = [];
        this.setEditableEl = (el) => {
            if (this.editableEl === el)
                return;
            this.editableEl = el;
            if (el)
                this.apply();
        };
        this.setBodyEl = (el) => {
            if (this.bodyEl === el)
                return;
            this.bodyEl = el;
            if (el)
                this.apply();
        };
        this.setField = (key, value) => {
            if (this.mutxt.readOnly.value)
                return;
            const current = this.cs.value;
            if (value === undefined) {
                if (current[key] === undefined)
                    return;
                const next = { ...current };
                delete next[key];
                const obj = this.read();
                if (obj)
                    obj.del([key]);
                this.cs.next(next);
                return;
            }
            if (current[key] === value)
                return;
            const obj = this.ensure();
            obj.add('/' + key, value);
            this.cs.next({ ...current, [key]: value });
        };
        this.replace = (next) => {
            if (this.mutxt.readOnly.value)
                return;
            const current = this.cs.value;
            const keysToRemove = [];
            for (const k in current)
                if (!(k in next))
                    keysToRemove.push(k);
            if ((0, guard_1.isEmptyCustomStyle)(next)) {
                const obj = this.read();
                if (obj && keysToRemove.length)
                    obj.del(keysToRemove);
                this.cs.next({});
                return;
            }
            const obj = this.ensure();
            if (keysToRemove.length)
                obj.del(keysToRemove);
            obj.mergeKeys(next);
            this.cs.next({ ...next });
        };
    }
    apply() {
        const cs = this.cs.value;
        const editable = this.editableEl;
        if (editable) {
            const style = editable.style;
            for (const prop of this.appliedEditableProps)
                style.removeProperty(prop);
            this.appliedEditableProps = [];
            const css = (0, css_1.toInlineCss)(cs);
            for (const k in css) {
                // Skip background on the editable. The body wrapper owns it. Painting
                // here too would double up under semi-transparent bg.
                if (k === 'backgroundColor')
                    continue;
                const v = css[k];
                if (v === undefined || v === null)
                    continue;
                const prop = k.replace(/([A-Z])/g, '-$1').toLowerCase();
                style.setProperty(prop, String(v));
                this.appliedEditableProps.push(prop);
            }
            const fgVars = (0, css_1.computeFgVars)(cs.fg, cs.bg);
            for (const k in fgVars) {
                style.setProperty(k, fgVars[k]);
                this.appliedEditableProps.push(k);
            }
            if (cs.lh !== undefined) {
                style.setProperty('--mutxt-lh', String(cs.lh));
                this.appliedEditableProps.push('--mutxt-lh');
            }
        }
        const body = this.bodyEl;
        if (body) {
            const style = body.style;
            for (const prop of this.appliedBodyProps)
                style.removeProperty(prop);
            this.appliedBodyProps = [];
            if (cs.bg) {
                style.setProperty('background-color', cs.bg);
                this.appliedBodyProps.push('background-color');
            }
        }
    }
    read() {
        if (this._obj)
            return this._obj;
        const docObj = this.mutxt.obj;
        if (!docObj.has('cs'))
            return undefined;
        const child = docObj.obj('/cs', true);
        if (!child)
            return undefined;
        this._obj = child;
        return this._obj;
    }
    ensure() {
        if (this._obj)
            return this._obj;
        const docObj = this.mutxt.obj;
        const existing = docObj.has('cs') ? docObj.obj('/cs', true) : undefined;
        if (!existing) {
            docObj.add('/cs', json_crdt_1.s.obj({}));
        }
        this._obj = docObj.obj('/cs');
        return this._obj;
    }
    refresh() {
        const obj = this.read();
        if (!obj) {
            if (Object.keys(this.cs.value).length)
                this.cs.next({});
            return;
        }
        const next = (0, guard_1.sanitizeCustomStyle)(obj.view()) ?? {};
        this.cs.next(next);
    }
    start() {
        const subscribe = (target) => target.onSubtreeChange(() => this.refresh());
        const initial = this.read();
        if (initial) {
            this.refresh();
            this.unsub = subscribe(initial);
        }
        else {
            const docObj = this.mutxt.obj;
            const docUnsub = docObj.onSubtreeChange(() => {
                const obj = this.read();
                if (!obj)
                    return;
                docUnsub();
                this.unsub = subscribe(obj);
                this.refresh();
            });
            this.unsub = docUnsub;
        }
        this.applyUnsub = this.cs.subscribe(() => this.apply());
        return () => {
            this.unsub?.();
            this.unsub = undefined;
            this.applyUnsub?.();
            this.applyUnsub = undefined;
        };
    }
}
exports.CustomStyleState = CustomStyleState;
